// hooks/index.js — all custom hooks in one file

import { useState, useEffect, useCallback, useRef } from "react";

/** Returns true when page has scrolled past `threshold` px */
export const useScrolled = (threshold = 40) => {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > threshold);
    window.addEventListener("scroll", fn, { passive: true });
    return () => window.removeEventListener("scroll", fn);
  }, [threshold]);
  return scrolled;
};

/** Debounces a value by `delay` ms — prevents firing on every keystroke */
export const useDebounce = (value, delay = 300) => {
  const [deb, setDeb] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDeb(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return deb;
};

/**
 * Wraps an async function and tracks loading / error / data.
 * Usage: const { execute, data, loading, error } = useAsync(api.submit);
 */
export const useAsync = (asyncFn) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const execute = useCallback(
    async (...args) => {
      setLoading(true);
      setError(null);
      try {
        const res = await asyncFn(...args);
        setData(res);
        return res;
      } catch (err) {
        setError(err.message);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [asyncFn],
  );
  const reset = useCallback(() => {
    setData(null);
    setError(null);
    setLoading(false);
  }, []);
  return { execute, data, loading, error, reset };
};

/** Calls `cb` when a click is detected outside `ref` */
export const useClickOutside = (ref, cb) => {
  useEffect(() => {
    const fn = (e) => {
      if (ref.current && !ref.current.contains(e.target)) cb();
    };
    document.addEventListener("mousedown", fn);
    return () => document.removeEventListener("mousedown", fn);
  }, [ref, cb]);
};
